/**
 * Finalise panel.
 *
 * Provides a pre-submission checklist, change reason selection,
 * reserve value preview (read from document SDT tags), and the
 * "Finalise Reserves" button that posts reserve deltas to Sharedo.
 *
 * Enhanced with AI extraction fallback for documents without SDT tags.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  Option,
  Text,
  Card,
  CardHeader,
  Divider,
  MessageBar,
  MessageBarBody,
  Spinner,
  Badge,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { CheckboxOnChangeData } from '@fluentui/react-components';
import { useGateway } from '../../api/useGateway';
import type {
  FinaliseResult,
  FinaliseTransaction,
  AdviceWorkflowSummary,
  ReadinessResult,
  MatterSummary,
  ReportingSchema,
  ReserveRow
} from '../../api/gateway-client';
import { GatewayError } from '../../api/gateway-client';
import { readReserveValues } from '../../office/sdt-reader';
import type { SdtScanResult } from '../../office/sdt-reader';

/** Props for the FinalisePanel component. */
interface FinalisePanelProps {
  matterId: string | null;
}

/** Change reason option from the reserve-change-reasons option set. */
interface ChangeReason {
  code: string;
  label: string;
}

/** The 16 change reason codes from sharedo-config/option-sets/reserve-change-reasons.json. */
const CHANGE_REASONS: ChangeReason[] = [
  { code: 'INITIAL', label: 'Initial Reserve' },
  { code: 'EXP-ORTHO', label: 'Expert Report - Orthopaedic' },
  { code: 'EXP-PSYCH', label: 'Expert Report - Psychiatric/Psychological' },
  { code: 'EXP-LIAB', label: 'Expert Report - Liability' },
  { code: 'PROG-PERM', label: 'Prognosis Change - Permanent' },
  { code: 'PROG-IMPROVE', label: 'Prognosis Change - Improvement' },
  { code: 'FEL-ADJ', label: 'Future Economic Loss Adjustment' },
  { code: 'PEL-ADJ', label: 'Past Economic Loss Adjustment' },
  { code: 'MED-ADJ', label: 'Medical Expenses Adjustment' },
  { code: 'CARE-ADJ', label: 'Care Costs Adjustment' },
  { code: 'LIAB-ADJ', label: 'Liability Assessment Change' },
  { code: 'CN-ADJ', label: 'Contributory Negligence Adjustment' },
  { code: 'SETTLE-OFFER', label: 'Settlement Offer Received/Made' },
  { code: 'SETTLE-AUTH', label: 'Settlement Authority Revised' },
  { code: 'AI-EXTRACTED', label: 'AI Extracted' },
  { code: 'ADMIN-CORRECTION', label: 'Administrative Correction' },
];

/** Checklist items that must be ticked before finalisation. */
const CHECKLIST_ITEMS = [
  'Reserve values reviewed and accurate',
  'Matter reference matches current document',
  'Change reason selected',
  'Partner has approved reserve changes',
] as const;

const BRAND_COLOR = '#075156';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    paddingBottom: '80px', // room for pinned submit section
  },

  // ── Readiness header strip ──────────────────────────────────────────────
  readinessStrip: {
    backgroundColor: BRAND_COLOR,
    color: '#fff',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  readinessStripReady: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  readinessStripBlocked: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  readinessLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#fff',
    flex: 1,
  },
  dotsRow: {
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.35)',
    flexShrink: 0,
  },
  dotFilled: {
    backgroundColor: '#fff',
  },

  // ── Section shell ───────────────────────────────────────────────────────
  section: {
    padding: '14px 14px 0 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  sectionDivider: {
    margin: '12px 0 0 0',
  },

  // ── Section header row ──────────────────────────────────────────────────
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
  },
  sectionTitleRequired: {
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
  },
  requiredStar: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: '2px',
  },

  // ── Checklist ───────────────────────────────────────────────────────────
  checklistWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 10px',
    borderRadius: '6px',
    backgroundColor: tokens.colorNeutralBackground2,
    transition: 'background-color 0.2s',
  },
  checklistWrapDone: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 2px',
    borderRadius: '4px',
    gap: '6px',
  },
  checkRowDone: {
    opacity: 0.7,
  },
  checkLabel: {
    fontSize: '13px',
    flex: 1,
    color: tokens.colorNeutralForeground1,
  },
  checkLabelDone: {
    textDecoration: 'line-through',
    color: tokens.colorNeutralForeground3,
  },
  checkmarkIcon: {
    fontSize: '14px',
    color: tokens.colorPaletteGreenForeground3,
    flexShrink: 0,
  },
  allConfirmedBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '6px',
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorPaletteGreenForeground3,
  },

  // ── Change Reason ───────────────────────────────────────────────────────
  dropdown: {
    width: '100%',
  },
  reasonSelected: {
    fontSize: '13px',
    fontWeight: 600,
    color: BRAND_COLOR,
    padding: '6px 10px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    marginTop: '6px',
  },

  // ── Reserve table ───────────────────────────────────────────────────────
  reserveHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '8px',
  },
  sourceBadge: {
    fontSize: '10px',
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
    padding: '1px 7px',
    borderRadius: '10px',
    fontWeight: 600,
  },
  iconButtonGroup: {
    display: 'flex',
    gap: '4px',
    marginLeft: 'auto',
  },
  previewTable: {
    width: '100%',
    fontSize: '12px',
    borderCollapse: 'collapse',
  },
  previewRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  previewCode: {
    padding: '5px 6px',
    color: tokens.colorNeutralForeground2,
  },
  previewAmount: {
    padding: '5px 6px',
    textAlign: 'right',
    fontFamily: 'monospace',
    color: tokens.colorNeutralForeground1,
  },
  previewTotal: {
    fontWeight: 700,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  previewTotalAmount: {
    padding: '7px 6px',
    textAlign: 'right',
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: '14px',
    color: BRAND_COLOR,
  },
  noWordInfo: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    padding: '8px 10px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
  },

  // ── Submit section ──────────────────────────────────────────────────────
  submitSection: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '12px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    zIndex: 10,
  },
  blockerCard: {
    padding: '10px 12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    marginBottom: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  blockerTitle: {
    fontSize: '11px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '2px',
  },
  blockerItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  blockerIcon: {
    flexShrink: 0,
    fontSize: '13px',
  },
  submitButton: {
    width: '100%',
    backgroundColor: BRAND_COLOR,
    color: '#fff',
    fontWeight: 700,
    fontSize: '14px',
    height: '40px',
    borderRadius: '6px',
  },
  submitButtonDisabled: {
    width: '100%',
    height: '40px',
    borderRadius: '6px',
    fontSize: '14px',
  },
});

/** Format a number as AUD currency. */
function formatCurrency(value: number): string {
  return value.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
  });
}

/** Get a client-specific label from the reporting schema if available. */
function getClientLabel(schema: ReportingSchema | undefined, path: string, defaultLabel: string): string {
  if (!schema) return defaultLabel;
  for (const reportType of Object.values(schema.reportTypes)) {
    const field = reportType.fields.find((f) => f.path === path);
    if (field) return field.label;
  }
  return defaultLabel;
}

/** Get whole document content as Base64 for AI processing. */
async function getDocumentBase64(): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: 65536 }, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(asyncResult.error.message));
        return;
      }

      const file = asyncResult.value;
      const sliceCount = file.sliceCount;
      const slices: string[] = [];
      let completed = 0;

      const getSlice = (index: number) => {
        file.getSliceAsync(index, (sliceResult) => {
          if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
            // Buffer.from is not available in browser, use btoa/Uint8Array or similar
            // But since we are in a taskpane, we can use TextDecoder if it was text,
            // but this is compressed (docx). For binary to base64 in browser:
            const base64 = btoa(
              new Uint8Array(sliceResult.value.data)
                .reduce((data, byte) => data + String.fromCharCode(byte), '')
            );
            slices[index] = base64;
            completed++;

            if (completed === sliceCount) {
              file.closeAsync();
              resolve(slices.join(''));
            } else {
              getSlice(index + 1);
            }
          } else {
            file.closeAsync();
            reject(new Error(sliceResult.error.message));
          }
        });
      };

      if (sliceCount === 0) {
        file.closeAsync();
        resolve('');
      } else {
        getSlice(0);
      }
    });
  });
}

/** Returns true if the error message looks like a "Word is not defined" / outside-Word context error. */
function isOutsideWordError(msg: string): boolean {
  return /word is not defined|office is not defined|not running in word|not in a word/i.test(msg);
}

/** Finalise panel with checklist, reason dropdown, SDT preview, and submit button. */
export const FinalisePanel: React.FC<FinalisePanelProps> = ({ matterId }) => {
  const styles = useStyles();
  const gateway = useGateway();
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Matter state (for schema)
  const [matter, setMatter] = useState<MatterSummary | null>(null);

  // Workflow state
  const [workflow, setWorkflow] = useState<AdviceWorkflowSummary | null>(null);

  // Readiness state
  const [readiness, setReadiness] = useState<ReadinessResult | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);

  // Extraction state
  const [useAI, setUseAI] = useState(false);
  const [scanResult, setScanResult] = useState<SdtScanResult | null>(null);
  const [aiReserves, setAiReserves] = useState<ReserveRow[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  // Build transactions from active source (SDT or AI)
  const transactions: FinaliseTransaction[] = useAI
    ? aiReserves
        .filter((r) => r.amount !== 0)
        .map((r) => ({ accountCode: r.accountCode, amount: r.amount }))
    : scanResult
        ? scanResult.reserves
            .filter((r) => r.amount !== 0)
            .map((r) => ({ accountCode: r.accountCode, amount: r.amount }))
        : [];

  const allChecked = checkedItems.size === CHECKLIST_ITEMS.length;
  const hasTransactions = transactions.length > 0;
  const readinessBlocked = readiness?.blocked === true;

  // Check document link on mount
  const [isDocumentLinked, setIsDocumentLinked] = useState<boolean | null>(null);
  const linkCheckRef = useRef(false);
  useEffect(() => {
    if (linkCheckRef.current) return;
    linkCheckRef.current = true;
    import('../../office/document-metadata').then(({ readMatterLink }) =>
      readMatterLink().then((link) => setIsDocumentLinked(!!link)).catch(() => setIsDocumentLinked(false))
    );
  }, []);

  const canFinalise = !!matterId && allChecked && selectedReason !== '' && hasTransactions && !isSubmitting && !readinessBlocked && isDocumentLinked === true;

  /** Scan the document for reserve SDT values. */
  const scanDocument = useCallback(async () => {
    setIsScanning(true);
    setScanError(null);

    try {
      const result = await readReserveValues();
      setScanResult(result);
      if (result.reserveControls > 0) {
        setUseAI(false);
      }
    } catch (err: unknown) {
      setScanError(
        err instanceof Error ? err.message : 'Failed to read document content controls',
      );
      setScanResult(null);
    } finally {
      setIsScanning(false);
    }
  }, []);

  /** Extract reserves using AI fallback. */
  const extractWithAI = useCallback(async () => {
    if (!matterId || !gateway || !matter) return;
    setIsAiProcessing(true);
    setScanError(null);

    try {
      const base64 = await getDocumentBase64();
      const result = await gateway.extractReserves({
        matterId,
        firmId: 'firm-1', // Default for demo
        clientId: matter.client?.id,
        documentBase64: base64,
        documentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      if (result.success) {
        setAiReserves(result.reserves);
        setUseAI(true);
      } else {
        throw new Error('AI extraction failed');
      }
    } catch (err: unknown) {
      setScanError(err instanceof Error ? err.message : 'AI extraction failed');
    } finally {
      setIsAiProcessing(false);
    }
  }, [matterId, gateway, matter]);

  // Load workflow status, readiness, and auto-scan on mount
  useEffect(() => {
    if (matterId && gateway) {
      scanDocument();

      gateway.getMatter(matterId)
        .then(setMatter)
        .catch(() => setMatter(null));

      gateway.getAdviceWorkflow(matterId)
        .then(setWorkflow)
        .catch(() => setWorkflow(null));

      setReadinessLoading(true);
      gateway.getReadiness(matterId)
        .then(setReadiness)
        .catch(() => setReadiness(null))
        .finally(() => setReadinessLoading(false));
    }
  }, [matterId, gateway, scanDocument]);

  const handleCheckChange = useCallback(
    (index: number) => (_ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
      setCheckedItems((prev) => {
        const next = new Set(prev);
        if (data.checked) {
          next.add(index);
        } else {
          next.delete(index);
        }
        return next;
      });
    },
    []
  );

  const handleFinalise = useCallback(async () => {
    if (!matterId || !gateway || transactions.length === 0) return;

    setIsSubmitting(true);
    setResult(null);

    try {
      const response: FinaliseResult = await gateway.finaliseReserves(matterId, {
        changeReasonCode: selectedReason,
        transactions,
        adviceTaskId: workflow?.pmsTaskId,
      });

      setResult({
        type: 'success',
        message: response.message || `Reserves finalised successfully (ID: ${response.transactionId}).`,
      });
      // Refresh workflow status
      if (gateway && matterId) {
        gateway.getAdviceWorkflow(matterId).then(setWorkflow).catch(() => {});
      }
    } catch (err: unknown) {
      const message =
        err instanceof GatewayError
          ? `${err.message} (HTTP ${err.statusCode})`
          : err instanceof Error
            ? err.message
            : 'An unexpected error occurred during finalisation.';
      setResult({ type: 'error', message });
    } finally {
      setIsSubmitting(false);
    }
  }, [matterId, gateway, selectedReason, transactions, workflow]);

  /** Get labels for fields in readiness report. */
  const getFieldLabels = (paths: string[]): string[] => {
    return paths.map(p => getClientLabel(matter?.reportingSchema, p, p));
  };

  /** Determine the appropriate disabled reason text. */
  const getDisabledReason = (): string | null => {
    if (!matterId) return 'Select a matter first';
    if (isDocumentLinked === false) return 'Link this document to a matter first (Matter tab).';
    if (readinessBlocked) {
      const blockers = getFieldLabels(readiness?.blockers || []);
      return `Finalisation is blocked: ${blockers.join(', ') || 'resolve all blockers before proceeding'}.`;
    }
    if (!hasTransactions) return 'No reserve values found in document.';
    if (!allChecked || selectedReason === '') {
      return 'Complete all checklist items and select a change reason before finalising.';
    }
    return null;
  };

  const disabledReason = getDisabledReason();

  // ── Computed readiness metrics ─────────────────────────────────────────
  // Count how many of the 4 "steps" are done:
  // 1. checklist, 2. change reason, 3. reserves present, 4. readiness/doc linked
  const stepsDone = [
    allChecked,
    selectedReason !== '',
    hasTransactions,
    !readinessBlocked && isDocumentLinked === true && !!matterId,
  ].filter(Boolean).length;

  const isReady = canFinalise;
  const isBlocked = readinessBlocked || isDocumentLinked === false || !matterId;

  // ── Scan error classification ──────────────────────────────────────────
  const isOutsideWord = scanError != null && isOutsideWordError(scanError);
  const realScanError = scanError && !isOutsideWord ? scanError : null;

  // ── Source label for reserves section ─────────────────────────────────
  const sourceLabel = useAI ? 'Source: AI Extraction' : 'Source: Document Tags';

  // ── Blocker list for submit section ───────────────────────────────────
  interface BlockerItem {
    icon: string;
    text: string;
  }
  const blockerItems: BlockerItem[] = [];
  if (!matterId) {
    blockerItems.push({ icon: '⬜', text: 'Select a matter' });
  }
  if (isDocumentLinked === false) {
    blockerItems.push({ icon: '🔴', text: 'Link this document to a matter (Matter tab)' });
  }
  if (readinessBlocked) {
    const bs = getFieldLabels(readiness?.blockers || []);
    blockerItems.push({ icon: '🔴', text: `Blocked: ${bs.join(', ') || 'resolve blockers'}` });
  }
  if (!hasTransactions) {
    blockerItems.push({ icon: '⚠️', text: 'No reserve values found — refresh tags or use AI extraction' });
  }
  if (!allChecked) {
    const remaining = CHECKLIST_ITEMS.length - checkedItems.size;
    blockerItems.push({ icon: '⬜', text: `Complete ${remaining} remaining checklist item${remaining !== 1 ? 's' : ''}` });
  }
  if (selectedReason === '') {
    blockerItems.push({ icon: '⬜', text: 'Select a change reason' });
  }

  return (
    <div className={styles.container}>

      {/* ── Readiness strip ─────────────────────────────────────────── */}
      <div
        className={[
          styles.readinessStrip,
          isReady ? styles.readinessStripReady : '',
          isBlocked ? styles.readinessStripBlocked : '',
        ].filter(Boolean).join(' ')}
      >
        <span className={styles.readinessLabel}>
          {isReady
            ? '✓ Ready to submit'
            : readinessLoading
            ? 'Checking readiness…'
            : `${stepsDone} of 4 steps complete`}
        </span>
        <div className={styles.dotsRow}>
          {[allChecked, selectedReason !== '', hasTransactions, isDocumentLinked === true && !readinessBlocked && !!matterId].map((done, i) => (
            <div
              key={i}
              className={[styles.dot, done ? styles.dotFilled : ''].filter(Boolean).join(' ')}
            />
          ))}
        </div>
      </div>

      {/* ── Step 1 — Checklist ──────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Step 1 — Pre-Submission Checklist</span>
          {allChecked && (
            <Badge appearance="filled" color="success" size="small">
              All confirmed
            </Badge>
          )}
        </div>

        <div className={[styles.checklistWrap, allChecked ? styles.checklistWrapDone : ''].filter(Boolean).join(' ')}>
          {allChecked ? (
            <div className={styles.allConfirmedBadge}>
              ✓&nbsp;All items confirmed
            </div>
          ) : (
            CHECKLIST_ITEMS.map((item, index) => {
              const done = checkedItems.has(index);
              return (
                <div key={index} className={[styles.checkRow, done ? styles.checkRowDone : ''].filter(Boolean).join(' ')}>
                  <Checkbox
                    checked={done}
                    onChange={handleCheckChange(index)}
                    aria-label={item}
                  />
                  <span className={[styles.checkLabel, done ? styles.checkLabelDone : ''].filter(Boolean).join(' ')}>
                    {item}
                  </span>
                  {done && <span className={styles.checkmarkIcon}>✓</span>}
                </div>
              );
            })
          )}
        </div>
      </div>

      <Divider className={styles.sectionDivider} />

      {/* ── Step 2 — Change Reason ──────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitleRequired}>
            Step 2 — Change Reason
            <span className={styles.requiredStar}>*</span>
          </span>
          {selectedReason && (
            <Badge appearance="filled" color="success" size="small">
              Selected
            </Badge>
          )}
        </div>

        <Dropdown
          className={styles.dropdown}
          placeholder="Select a reason for this reserve change…"
          value={CHANGE_REASONS.find((r) => r.code === selectedReason)?.label ?? ''}
          onOptionSelect={(_ev, data) => setSelectedReason(data.optionValue ?? '')}
        >
          {CHANGE_REASONS.map((reason) => (
            <Option key={reason.code} value={reason.code}>
              {reason.label}
            </Option>
          ))}
        </Dropdown>

        {selectedReason && (
          <div className={styles.reasonSelected}>
            {CHANGE_REASONS.find((r) => r.code === selectedReason)?.label}
          </div>
        )}
      </div>

      <Divider className={styles.sectionDivider} />

      {/* ── Step 3 — Document Reserves ──────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Step 3 — Document Reserves</span>
          <div className={styles.iconButtonGroup}>
            <Button
              appearance="subtle"
              size="small"
              disabled={isScanning}
              onClick={scanDocument}
              title="Refresh Tags"
            >
              {isScanning ? <Spinner size="tiny" /> : '↻ Tags'}
            </Button>
            <Button
              appearance="subtle"
              size="small"
              disabled={isAiProcessing}
              onClick={extractWithAI}
              title="Extract via AI"
            >
              {isAiProcessing ? <Spinner size="tiny" /> : '✦ AI'}
            </Button>
          </div>
        </div>

        {/* Source badge */}
        {(scanResult || aiReserves.length > 0) && !isScanning && !isAiProcessing && (
          <div className={styles.reserveHeaderRow}>
            <span className={styles.sourceBadge}>{sourceLabel}</span>
          </div>
        )}

        {/* Outside-Word info (not an error) */}
        {isOutsideWord && (
          <div className={styles.noWordInfo}>
            Running outside Word — open this document in Word to read reserve values from document tags.
          </div>
        )}

        {/* Real scan error */}
        {realScanError && (
          <MessageBar intent="error">
            <MessageBarBody>{realScanError}</MessageBarBody>
          </MessageBar>
        )}

        {/* Loading */}
        {(isScanning || isAiProcessing) && (
          <Spinner size="tiny" label={isScanning ? 'Scanning tags…' : 'AI analysing document…'} />
        )}

        {/* Reserve table */}
        {!isScanning && !isAiProcessing && transactions.length > 0 && (
          <table className={styles.previewTable}>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.accountCode} className={styles.previewRow}>
                  <td className={styles.previewCode}>
                    {getClientLabel(matter?.reportingSchema, tx.accountCode, tx.accountCode)}
                  </td>
                  <td className={styles.previewAmount}>{formatCurrency(tx.amount)}</td>
                </tr>
              ))}
              <tr className={styles.previewTotal}>
                <td className={styles.previewCode}>
                  <strong>Total</strong>
                  <span style={{ fontWeight: 400, color: tokens.colorNeutralForeground3, marginLeft: 6, fontSize: 11 }}>
                    ({transactions.length} item{transactions.length !== 1 ? 's' : ''})
                  </span>
                </td>
                <td className={styles.previewTotalAmount}>
                  {formatCurrency(transactions.reduce((s, t) => s + t.amount, 0))}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* No reserves and not loading, not outside-Word */}
        {!isScanning && !isAiProcessing && transactions.length === 0 && !isOutsideWord && (
          <MessageBar intent="info">
            <MessageBarBody>
              No reserve values found. Ensure values are entered in the document tables, or use ✦ AI extraction.
            </MessageBarBody>
          </MessageBar>
        )}
      </div>

      {/* Readiness warning (missing fields, not blocked) */}
      {readiness && !readinessLoading && !readiness.blocked && readiness.readinessPct < 100 && (
        <div className={styles.section}>
          <MessageBar intent="warning">
            <MessageBarBody>
              Readiness: {readiness.readinessPct}%.
              {readiness.missing.length > 0 && ` Missing: ${getFieldLabels(readiness.missing).join(', ')}.`}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Submission result */}
      {result && (
        <div className={styles.section}>
          <MessageBar intent={result.type === 'success' ? 'success' : 'error'}>
            <MessageBarBody>{result.message}</MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* ── Pinned submit section ────────────────────────────────────── */}
      <div className={styles.submitSection}>
        {/* What's next card when not ready */}
        {!canFinalise && blockerItems.length > 0 && (
          <div className={styles.blockerCard}>
            <div className={styles.blockerTitle}>What's next</div>
            {blockerItems.map((b, i) => (
              <div key={i} className={styles.blockerItem}>
                <span className={styles.blockerIcon}>{b.icon}</span>
                <span>{b.text}</span>
              </div>
            ))}
          </div>
        )}

        {canFinalise ? (
          <Button
            className={styles.submitButton}
            appearance="primary"
            disabled={isSubmitting}
            onClick={handleFinalise}
            icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
            style={{ backgroundColor: BRAND_COLOR }}
          >
            {isSubmitting ? 'Finalising…' : `Finalise Reserves →`}
          </Button>
        ) : (
          <Button
            className={styles.submitButtonDisabled}
            appearance="secondary"
            disabled
          >
            {isSubmitting ? 'Finalising…' : `Finalise Reserves (${transactions.length} item${transactions.length !== 1 ? 's' : ''})`}
          </Button>
        )}
      </div>
    </div>
  );
};
